import { put, select, takeLatest } from 'redux-saga/effects';
import { mapActions as actions } from '.';
import * as L from 'leaflet';
import {
  FieldsBoundaries,
  IFieldsCultureVariations,
  IFieldsResponse,
  ILoadPolygonsPayload,
  IMapCulture,
  IMappedField,
  IPolygon,
  ITempPolygon,
} from './types';
import { globalActions } from 'app/containers/Global/slice';
import {
  selectPolygonByFieldId,
  selectCoordinatesArr,
  selectSingleFieldOnMap,
  selectTempPolygonById,
  selectMapCultures,
  selectDrawnPolygonById,
} from './selectors';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  IDrawnPolygon,
  ITempFieldResponse,
} from 'app/containers/Field/slice/types';
import { mapFieldsResponseData, reorder } from './helpers';
import mapApi from '../mapApi';
import { FIELDS_LAYER_ID } from './map-constants';

function* loadPolygonsSaga(action: PayloadAction<ILoadPolygonsPayload>) {
  try {
    const { envId, seasonId, companyId, companyGroupId } = action.payload;

    const data: IFieldsResponse | undefined = yield mapApi.getPolygons(
      envId,
      seasonId,
      companyId,
      companyGroupId,
    );

    if (!data) {
      yield put(actions.setPolygons([]));
      return;
    }
    const fieldSeasonIds = data.features.map(feature => feature.properties.id);

    const fieldsCultureVariations: IFieldsCultureVariations = yield mapApi.getFieldCultureVariationsByUsedResource(
      {
        envId,
        fieldSeasonIds,
      },
    );

    const polygons: IPolygon[] = mapFieldsResponseData(
      data,
      fieldsCultureVariations,
    );

    yield put(actions.setPolygons(polygons));

    const mapCultures: IMapCulture[] = yield select(selectMapCultures);

    const culturesIdsArr = mapCultures.map(culture => culture.cultureId);
    yield put(actions.setSelectedCultureMenuIds(culturesIdsArr));

    const variationsIdsArr = mapCultures.reduce(
      (accumulator: number[], cultureItem) => {
        const variationsIds = cultureItem.cultureVariations.map(
          variation => variation.cultureVariation.id,
        );
        accumulator.push(...variationsIds);
        return accumulator;
      },
      [],
    );
    yield put(actions.setSelectedCultureMenuVariationsIds(variationsIdsArr));
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setMapLoading(false));
  }
}

function* fitAllPolygonsOnMapSaga() {
  try {
    const coordArrInArr: FieldsBoundaries[] = yield select(
      selectCoordinatesArr,
    );

    const coordArr = coordArrInArr.flat(3);

    if (coordArr.length) {
      yield put(actions.setFitCoordArrOnMap(coordArr));
    }
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

function* getTempFieldsFromTempFieldResponseSaga(
  action: PayloadAction<ITempFieldResponse>,
) {
  try {
    const response = action.payload;

    const tempPolygons: ITempPolygon[] = response.features.map(feature => {
      const coordinatesReordered = reorder(feature.geometry.coordinates);
      const markerCenter = L.polygon(coordinatesReordered)
        .getBounds()
        .getCenter();
      return {
        type: feature.geometry.type,
        propertiesJson: JSON.stringify(feature.properties),
        id: feature.id,
        coordinates: coordinatesReordered,
        weight: 1,
        layerId: FIELDS_LAYER_ID,
        markerPosition: [markerCenter.lat, markerCenter.lng],
      };
    });

    yield put(actions.setTempPolygons(tempPolygons));
    yield put(actions.fitAllPolygonsOnMap());
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

function* zoomOnTempPolygonSaga(action: PayloadAction<string>) {
  try {
    const polygonId = action.payload;

    const polygon: ITempPolygon | undefined = yield select(state =>
      selectTempPolygonById(state, polygonId),
    );

    if (polygon) {
      const coordArr = polygon.coordinates.flat(2);

      if (coordArr.length) {
        yield put(actions.setFitCoordArrOnMap(coordArr));
      }
    }
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

function* zoomOnDrawnPolygonSaga(action: PayloadAction<string>) {
  try {
    const polygonId = action.payload;

    const polygon: IDrawnPolygon | undefined = yield select(state =>
      selectDrawnPolygonById(state, polygonId),
    );

    if (polygon) {
      const coordArr = polygon.latlngs.flat(2);

      if (coordArr.length) {
        yield put(actions.setFitCoordArrOnMap(coordArr));
      }
    }
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

function* zoomOnPolygonSaga(action: PayloadAction<number>) {
  try {
    const polygonFieldId = action.payload;

    const polygon: ITempPolygon | undefined = yield select(state =>
      selectPolygonByFieldId(state, polygonFieldId),
    );

    if (polygon) {
      const coordArr = polygon.coordinates.flat(2);

      if (coordArr.length) {
        yield put(actions.setFitCoordArrOnMap(coordArr));
      }
    }
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

function* zoomSingleFieldMapSaga() {
  try {
    const field: IMappedField | null = yield select(selectSingleFieldOnMap);

    if (field) {
      const coordArr = field.coordinates.flat(2);
      if (coordArr.length) {
        yield put(actions.setFitCoordArrOnMap(coordArr));
      }
    }
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

export function* mapSaga() {
  yield takeLatest(actions.zoomSingleFieldMap.type, zoomSingleFieldMapSaga);

  yield takeLatest(actions.onLoad.type, loadPolygonsSaga);
  yield takeLatest(actions.reloadPolygons.type, loadPolygonsSaga);

  yield takeLatest(actions.fitAllPolygonsOnMap.type, fitAllPolygonsOnMapSaga);

  yield takeLatest(actions.zoomOnPolygon.type, zoomOnPolygonSaga);
  yield takeLatest(actions.zoomOnTempPolygon.type, zoomOnTempPolygonSaga);
  yield takeLatest(actions.zoomOnDrawnPolygon.type, zoomOnDrawnPolygonSaga);

  yield takeLatest(
    actions.getTempFieldsFromTempFieldResponse.type,
    getTempFieldsFromTempFieldResponseSaga,
  );
}
